import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { fetchDostawcy, usunDostawce, zalozDostawce, zmienDostawce, type Dostawca } from '@/lib/suppliers-api';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  SŁOWNIK DOSTAWCÓW — mały moduł przy artykułach, jak biblioteka zdjęć.
 *
 *  ⚠️⚠️ ZMIERZONE NA MASTERZE TYCO (22.09.2026): cały słownik to DWA wpisy, w obu nazwa równa się
 *  numerowi, a adres, telefon i faks są puste. Dlatego moduł, a nie własna zakładka: ekran na
 *  dwa wiersze, w którym jedna kolumna powtarza drugą, odwiedzałoby się raz w życiu.
 *
 *  ⚠️⚠️ NUMERU NIE DA SIĘ ZMIENIĆ PO ZAŁOŻENIU, bo jest kluczem głównym w SPS. Zmiana znaczyłaby
 *  skasowanie i założenie od nowa, a wtedy każdy towar wskazujący na stary numer zostałby
 *  z odnośnikiem do nikogo — w SPS nie ma tam klucza obcego, który by tego pilnował.
 *
 *  ⚠️ KASOWANIE Z POTWIERDZENIEM (od 23.09.2026): „jeżeli artykuły mają przypisanego dostawcę, to zrób
 *  ostrzeżenie z potwierdzeniem (…) po usunięciu dostawcy wywalamy go z artykułów". Pytanie niesie
 *  liczbę towarów, a portal zdejmuje dostawcę z nich w tej samej operacji (`SuppliersService.usun`).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function ZarzadzanieDostawcami({
  groupId,
  mozeZapisywac,
  onClose,
}: {
  groupId: string;
  mozeZapisywac: boolean;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const toast = useToast();
  const qc = useQueryClient();
  const [edytowany, setEdytowany] = useState<Dostawca | null>(null);
  const [nowy, setNowy] = useState(false);

  const dane = useQuery({ queryKey: ['dostawcy', groupId], queryFn: () => fetchDostawcy(groupId) });
  const dostawcy = dane.data?.dostawcy ?? [];

  /**
   * ⚠️⚠️ PYTANIE Z LICZBĄ TOWARÓW, gdy dostawca je ma — „usunąć?" bez niej nie mówi, że zniknie on
   * też z artykułów. Zgoda jedzie do portalu (`wyczyscTowary`), a ten odmawia bez niej.
   */
  const usun = useMutation({
    mutationFn: (d: Dostawca) => usunDostawce(groupId, d.id, d.towarow > 0),
    onSuccess: async (_w, d) => {
      toast.success(t('dost.usunieto').replace('{nazwa}', d.name ?? d.supplierNumber));
      await qc.invalidateQueries({ queryKey: ['dostawcy', groupId] });
      await qc.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const zapytajIUsun = async (d: Dostawca) => {
    const nazwa = d.name ?? d.supplierNumber;
    const pytanie =
      d.towarow > 0
        ? t('dost.usunZTowarami').replace('{nazwa}', nazwa).replace('{n}', String(d.towarow))
        : t('dost.usunPytanie').replace('{nazwa}', nazwa);
    if (await zapytaj({ tytul: t('dost.usun'), tresc: pytanie, etykietaPotwierdz: t('dost.usun'), ton: 'grozne' })) usun.mutate(d);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {oknoPytania}
      <DialogContent title={t('dost.tytul')} className="max-w-2xl">
        <div className="space-y-3">
          <div className="flex items-center justify-end gap-3">
            {mozeZapisywac && (
              <Button size="sm" onClick={() => setNowy(true)}>
                <Plus className="size-4" /> {t('dost.dodaj')}
              </Button>
            )}
          </div>

          {dane.isPending ? (
            <p className="text-sm text-muted-foreground">
              <Loader2 className="inline size-4 animate-spin" />
            </p>
          ) : dostawcy.length === 0 ? (
            /* ⚠️ Pusty słownik to normalny stan wyspy, nie usterka — patrz nagłówek. */
            <p className="text-sm text-muted-foreground">{t('dost.pusto')}</p>
          ) : (
            <ul className="divide-y divide-border rounded border border-border">
              {dostawcy.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{d.name ?? d.supplierNumber}</span>
                    <span className="block truncate font-mono text-[11px] text-muted-foreground">
                      {d.supplierNumber}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {t('dost.towarow').replace('{n}', String(d.towarow))}
                    </span>
                    {/*
                      ⚠️⚠️ DOSTAWCA, KTÓREGO NIE DA SIĘ PRZYPISAĆ, MUSI BYĆ WIDOCZNY. W SPS klucz
                      ma 255 znaków, a kolumna na towarze 32 — taki wpis jest poprawny i zarazem
                      bezużyteczny. Bez tej etykiety wyglądałby jak każdy inny, a nie dawał się
                      wybrać na towarze bez podanego powodu.
                    */}
                    {!d.doPrzypisania && (
                      <span className="text-xs text-[var(--warning)]">{t('dost.zaDlugi')}</span>
                    )}
                    {mozeZapisywac && (
                      <Button size="icon" variant="ghost" onClick={() => setEdytowany(d)} aria-label={t('common.edit')}>
                        <Pencil className="size-4" />
                      </Button>
                    )}
                    {mozeZapisywac && (
                      <Button
                        size="icon"
                        variant="ghost"
                        disabled={usun.isPending}
                        onClick={() => zapytajIUsun(d)}
                        aria-label={`${t('dost.usun')} ${d.name ?? d.supplierNumber}`}
                        title={t('dost.usun')}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* ⚠️ Towary z numerem spoza słownika — stan u klienta poprawny, ale wart jednego zdania. */}
          {(dane.data?.spozaSlownika ?? 0) > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('dost.spozaSlownika').replace('{n}', String(dane.data!.spozaSlownika))}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {(nowy || edytowany !== null) && (
        <OknoDostawcy
          groupId={groupId}
          edytowany={edytowany}
          onClose={() => {
            setNowy(false);
            setEdytowany(null);
          }}
        />
      )}
    </Dialog>
  );
}

function OknoDostawcy({
  groupId,
  edytowany,
  onClose,
}: {
  groupId: string;
  edytowany: Dostawca | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [numer, setNumer] = useState(edytowany?.supplierNumber ?? '');
  const [nazwa, setNazwa] = useState(edytowany?.name ?? '');
  const [adres, setAdres] = useState([
    edytowany?.addr1 ?? '',
    edytowany?.addr2 ?? '',
    edytowany?.addr3 ?? '',
    edytowany?.addr4 ?? '',
  ]);

  const zapisz = useMutation({
    mutationFn: async () => {
      const opis = {
        name: nazwa.trim(),
        addr1: adres[0]!.trim(),
        addr2: adres[1]!.trim(),
        addr3: adres[2]!.trim(),
        addr4: adres[3]!.trim(),
      };
      return edytowany === null
        ? zalozDostawce(groupId, { supplierNumber: numer.trim(), ...opis })
        : zmienDostawce(groupId, edytowany.id, opis);
    },
    onSuccess: async () => {
      toast.success(t('dost.zapisano'));
      await qc.invalidateQueries({ queryKey: ['dostawcy', groupId] });
      await qc.invalidateQueries({ queryKey: ['items'] });
      onClose();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={edytowany === null ? t('dost.dodaj') : t('dost.edycja')}>
        <div className="space-y-3">
          {/*
            ⚠️⚠️ NUMER TYLKO PRZY ZAKŁADANIU. Jest kluczem głównym w SPS, więc jego zmiana to
            skasowanie i założenie od nowa — a wtedy towary wskazujące na stary numer zostają
            z odnośnikiem do nikogo.
          */}
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">{t('dost.numer')}</span>
            <Input
              value={numer}
              maxLength={32}
              disabled={edytowany !== null}
              onChange={(e) => setNumer(e.target.value)}
            />
            {edytowany === null && <span className="text-[11px] text-muted-foreground">{t('dost.numerHint')}</span>}
          </label>

          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">{t('dost.nazwa')}</span>
            <Input value={nazwa} maxLength={64} onChange={(e) => setNazwa(e.target.value)} />
          </label>

          {adres.map((v, i) => (
            <label key={i} className="block space-y-1">
              <span className="text-xs text-muted-foreground">{t('dost.adres').replace('{n}', String(i + 1))}</span>
              <Input
                value={v}
                maxLength={100}
                onChange={(e) => setAdres(adres.map((x, j) => (j === i ? e.target.value : x)))}
              />
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={zapisz.isPending || numer.trim() === ''} onClick={() => zapisz.mutate()}>
            {zapisz.isPending && <Loader2 className="animate-spin" />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
